import { useState } from "react";
import { API } from "../App";
import axios from "axios";
import { Phone, CheckCircle, Shield, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";

const SMSOptIn = () => {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!phone || phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    
    if (!agreed) {
      toast.error("Please agree to receive SMS notifications");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/sms-optin`, {
        phone: "+1" + phone.replace(/\D/g, ""),
        name: name || "Guest"
      });
      setSubmitted(true);
      toast.success("You're now subscribed to SMS notifications!");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">You're Subscribed!</h1>
            <p className="text-slate-600 mb-6">
              You will now receive SMS notifications from AV Realty Management for rent reminders, 
              maintenance updates, and important property notices.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
              <p><strong>To opt-out:</strong> Reply STOP to any message</p>
              <p><strong>For help:</strong> Reply HELP or call (847) 962-0778</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <div className="bg-green-800 text-white py-6 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-2xl font-bold">AV Realty Management</h1>
          <p className="text-green-100 mt-1">Property Management Services</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-3">SMS Notifications</h2>
          <p className="text-lg text-slate-600">
            Stay informed about your property with text message notifications
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg p-4 border text-center">
            <div className="text-2xl mb-2">📅</div>
            <h3 className="font-semibold text-slate-900">Rent Reminders</h3>
            <p className="text-sm text-slate-500">Never miss a due date</p>
          </div>
          <div className="bg-white rounded-lg p-4 border text-center">
            <div className="text-2xl mb-2">🔧</div>
            <h3 className="font-semibold text-slate-900">Maintenance Updates</h3>
            <p className="text-sm text-slate-500">Work order notifications</p>
          </div>
          <div className="bg-white rounded-lg p-4 border text-center">
            <div className="text-2xl mb-2">📋</div>
            <h3 className="font-semibold text-slate-900">Lease Notices</h3>
            <p className="text-sm text-slate-500">Renewal reminders</p>
          </div>
        </div>

        {/* Opt-In Form */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Your Name (Optional)
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Mobile Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    placeholder="(555) 123-4567"
                    className="pl-10 w-full"
                    maxLength={14}
                    required
                  />
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 rounded border-slate-300"
                    required
                  />
                  <span className="text-sm text-slate-600">
                    I agree to receive SMS text messages from AV Realty Management at the phone number provided. 
                    Messages may include rent reminders, maintenance updates, lease notifications, and other 
                    property-related information. Message frequency varies. Message and data rates may apply. 
                    Reply STOP to unsubscribe at any time. Reply HELP for assistance.
                  </span>
                </label>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-green-700 hover:bg-green-800 py-6 text-lg"
                disabled={loading}
              >
                {loading ? "Subscribing..." : "Subscribe to SMS Notifications"}
              </Button>
            </form>

            {/* Trust Indicators */}
            <div className="mt-6 pt-6 border-t flex items-center justify-center gap-2 text-sm text-slate-500">
              <Shield className="w-4 h-4" />
              <span>Your information is secure and will never be shared</span>
            </div>
          </CardContent>
        </Card>

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p className="mb-2">
            <strong>AV Realty Management</strong>
          </p>
          <p>Phone: (847) 962-0778 | Email: contact@avrealtymgmt.com</p>
          <div className="mt-4 space-x-4">
            <a href="/privacy" className="text-green-700 hover:underline">Privacy Policy</a>
            <a href="/terms" className="text-green-700 hover:underline">Terms of Service</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSOptIn;
